/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect, ReactElement, useContext } from 'react'

import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  PageHeader,
  Row,
  Skeleton,
  Table,
} from 'antd'
import { SelectFieldAnt, SelectItemProps } from 'Components/Form/Select'
import ID_COMPONENTE from 'config/idComponente'
import { ApplicationContext } from 'Context'
import Claims from 'Hooks/usePermissions/Claims'
import usePermissions from 'Hooks/usePermissions/usePermissions'
import { IMacro, IMacroTipo, IMacroViagem } from 'Models/docas'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import WsDocasService from 'Services/WS_DOCAS'

interface IProps {
  atualizaAgenda: (
    limparSelecao?: boolean,
    executandoAcao?: boolean,
  ) => Promise<void>
}
const ModalPesquisaInclusaoMacros = ({
  atualizaAgenda,
}: IProps): ReactElement => {
  const hookForm = useForm()

  const { applicationContext } = useContext(ApplicationContext)

  const [chavePesquisa, setChavePesquisa] = useState<string>()
  const [valorPesquisa, setValorPesquisa] = useState<string>()

  const [macroSelecionada, setMacroSelecionada] = useState<IMacro>()
  const [macroOutrosSelecionada, setMacroOutrosSelecionada] =
    useState<boolean>(false)
  const [justificativaMacro, setJustificativaMacro] = useState<string>()

  const [tiposMacros, setTiposMacros] = useState<IMacro[]>([])
  const [tiposMacrosPesquisa, setTiposMacrosPesquisa] = useState<IMacroTipo[]>(
    [],
  )
  const [selectTiposMacros, setSelectTiposMacros] = useState<SelectItemProps[]>(
    [],
  )
  const [selectChavesPesquisa, setSelectChavesPesquisa] = useState<
    SelectItemProps[]
  >([])

  const [carregandoTiposMacros, setCarregandoTiposMacros] =
    useState<boolean>(true)
  const [carregandoPesquisa, setCarregandoPesquisa] = useState<boolean>(false)
  const [carregandoInclusao, setCarregandoInclusao] = useState<boolean>(false)

  const [macrosViagem, setMacrosViagem] = useState<IMacroViagem[]>([])
  const [sucessoPesquisa, setSucessoPesquisa] = useState<boolean>()
  const [cdViagem, setCdViagem] = useState<number>()
  const [numeroViagem, setNumeroViagem] = useState<number>()
  const [placaVeiculo, setPlacaVeiculo] = useState<string>()

  const { isAuthorized, isSystemMaster } = usePermissions()

  useEffect(() => {
    getChavesPesquisa()
    getTiposMacros()
    getTiposMacrosPesquisa()
  }, [])

  const getChavesPesquisa = (): void => {
    const selectChavesPesquisa: SelectItemProps[] = [
      {
        value: 'numeroDocumento',
        label: applicationContext.textoDocumentoViagem!,
      },
      { value: 'numeroViagem', label: 'Número da Viagem' },
      { value: 'placa', label: 'Placa do Veículo' },
    ]

    setSelectChavesPesquisa(selectChavesPesquisa)
  }

  const getTiposMacros = async (): Promise<void> => {
    const { data } = await WsDocasService.macro.getList(ID_COMPONENTE.DOCAS)

    if (data.items) {
      setTiposMacros(data.items)

      const selectTiposMacros: SelectItemProps[] = data.items.map(
        (item: IMacro) => {
          return {
            value: item.cdMacro!.toString(),
            label: item.nome!,
          }
        },
      )

      setSelectTiposMacros(selectTiposMacros)
    }

    setCarregandoTiposMacros(false)
  }

  const getTiposMacrosPesquisa = async (): Promise<void> => {
    const { data } = await WsDocasService.macro.getTiposMacroViagemPesquisa(
      ID_COMPONENTE.DOCAS,
    )

    if (data.items) {
      setTiposMacrosPesquisa(data.items)
    }
  }

  const pesquisaMacros = async (): Promise<void> => {
    if (chavePesquisa && valorPesquisa) {
      try {
        const { data } = await WsDocasService.macro.buscaRegistrosMacroViagem(
          ID_COMPONENTE.DOCAS,
          chavePesquisa,
          valorPesquisa,
        )

        if (data.items) {
          const macrosViagem: IMacroViagem[] = []

          for (let i = 0; i < data.items.length; i++) {
            const macro: IMacroViagem = data.items[i]

            macro.dtAcaoStr = moment(macro.dtAcao).format('DD/MM/YYYY HH:mm:ss')

            macro.nomeMacro = tiposMacrosPesquisa.find(
              (tipoMacro) => tipoMacro.cdMacro === macro.cdMacro,
            )!.nome

            macrosViagem.push(macro)
          }

          setMacrosViagem(macrosViagem)
          setCdViagem(data.cdViagem)
          setNumeroViagem(data.numeroViagem)
          setPlacaVeiculo(data.placa ?? 'Sem placa')

          setSucessoPesquisa(true)
        } else {
          setMacrosViagem([])
          setSucessoPesquisa(false)
        }
      } catch (e) {
        console.error(e)

        setMacrosViagem([])
        setSucessoPesquisa(false)
      }
    }

    setCarregandoPesquisa(false)
  }

  const registraMacro = async (): Promise<void> => {
    if (macroSelecionada && cdViagem) {
      const dtAtual: moment.Moment = moment()

      const macro: IMacroViagem = {
        cdMacro: macroSelecionada.cdMacro!,
        justificativa: justificativaMacro,
        cdViagem: cdViagem,
        cdUsuario: applicationContext.usuarioAtual?.cdUsuario ?? 0,
        usuarioNome: applicationContext.usuarioAtual?.nome,
        dtAcao: dtAtual.format('YYYY-MM-DD HH:mm:ss'),
      }

      try {
        const { data } = await WsDocasService.macro.registraMacroViagem(
          ID_COMPONENTE.DOCAS,
          macro,
        )

        if (data.statusCode === 501) {
          macro.dtAcaoStr = dtAtual.format('DD/MM/YYYY HH:mm:ss')

          macro.nomeMacro = tiposMacros.find(
            (tipoMacro) => tipoMacro.cdMacro === macroSelecionada.cdMacro,
          )!.nome

          setMacrosViagem([...macrosViagem, macro])

          atualizaAgenda()
        } else {
          window.alert(`Erro no registro de macro. ${data.message}`)
        }
      } catch (e) {
        console.error(e)
        alert(
          'Ocorreu um erro para registrar a macro, favor contatar o suporte do Compulog',
        )
      }
    }

    setCarregandoInclusao(false)
  }

  const selecionaMacro = (cdMacro: number): void => {
    const macroSelecionada: IMacro = tiposMacros.find(
      (macro) => macro.cdMacro === cdMacro,
    )!

    setMacroSelecionada(macroSelecionada)
    setMacroOutrosSelecionada(macroSelecionada.nome!.toUpperCase() === 'OUTROS')
  }

  return (
    <>
      <PageHeader
        ghost={false}
        title="Pesquisa/Inclusão de Macros"
        extra={
          sucessoPesquisa !== undefined
            ? [
                <Form.Item
                  name="placaVeiculoNumeroViagem"
                  label="Placa do Veículo / Número da Viagem:"
                  key="placaVeiculoNumeroViagem"
                  style={{ marginRight: '20px' }}
                >
                  {sucessoPesquisa ? (
                    <>
                      <Input
                        type="text"
                        disabled={true}
                        style={{ backgroundColor: '#0080007a', color: 'white' }}
                        value={`${placaVeiculo} / ${numeroViagem}`}
                      />
                      <CheckOutlined
                        style={{
                          color: '#0080007a',
                          marginLeft: '7px',
                          fontSize: '20px',
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        type="text"
                        disabled={true}
                        style={{ backgroundColor: '#ff000057' }}
                      />
                      <CloseOutlined
                        style={{
                          color: '#ff000057',
                          marginLeft: '7px',
                          fontSize: '20px',
                        }}
                      />
                    </>
                  )}
                </Form.Item>,
              ]
            : []
        }
      />

      <Row gutter={16}>
        <Col span={10}>
          <SelectFieldAnt
            methods={hookForm}
            scope="form-pesquisa-macros"
            label="Pesquisar viagem por:"
            name="chavePesquisa"
            items={selectChavesPesquisa}
            onChangeValue={(value: string) => setChavePesquisa(value)}
          />
        </Col>

        <Col span={11}>
          <Form.Item name="parametroPesquisa" label="Parâmetro de Pesquisa:">
            <Input
              type="text"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setValorPesquisa(event.target.value)
              }
            />
          </Form.Item>
        </Col>

        <Col span={3}>
          <Button
            type="primary"
            disabled={
              !chavePesquisa || !valorPesquisa || valorPesquisa.length === 0
            }
            loading={carregandoPesquisa}
            onClick={() => {
              setCarregandoPesquisa(true)
              pesquisaMacros()
            }}
          >
            Pesquisar
          </Button>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Table
            size="small"
            pagination={false}
            scroll={{ y: 240 }}
            columns={[
              {
                title: 'Data/Hora',
                key: 'dtAcaoStr',
                dataIndex: 'dtAcaoStr',
              },
              {
                title: 'Macro',
                key: 'nomeMacro',
                dataIndex: 'nomeMacro',
              },
              {
                title: 'Usuário',
                key: 'usuarioNome',
                dataIndex: 'usuarioNome',
              },
              {
                title: 'Justificativa',
                key: 'justificativa',
                dataIndex: 'justificativa',
              },
            ]}
            dataSource={macrosViagem}
          />
        </Col>
      </Row>

      {isAuthorized(Claims.EmitirMacrosDocas) && (
        <>
          <Divider>Registrar nova macro para a viagem</Divider>

          <Skeleton loading={carregandoTiposMacros}>
            <Row gutter={16}>
              <Col span={10}>
                <SelectFieldAnt
                  methods={hookForm}
                  scope="form-registra-macro"
                  label="Macro:"
                  name="macro"
                  items={selectTiposMacros}
                  showSearch={true}
                  onChangeValue={(value: string) =>
                    selecionaMacro(window.parseInt(value))
                  }
                />
              </Col>

              <Col span={11}>
                <Form.Item
                  name="justificativaMacro"
                  label="Justificativa:"
                  required={macroOutrosSelecionada}
                >
                  <Input
                    type="text"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setJustificativaMacro(event.target.value)
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={3}>
                <Button
                  type="primary"
                  disabled={
                    !sucessoPesquisa ||
                    !macroSelecionada ||
                    (macroOutrosSelecionada &&
                      (!justificativaMacro || justificativaMacro.length === 0))
                  }
                  loading={carregandoInclusao}
                  onClick={() => {
                    setCarregandoInclusao(true)
                    registraMacro()
                  }}
                >
                  Registrar
                </Button>
              </Col>
            </Row>
          </Skeleton>
        </>
      )}
    </>
  )
}

export default ModalPesquisaInclusaoMacros
